import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "swishr_cookie_consent";

// Read the stored consent choice. Returns "all" | "essential" | null.
export const getCookieConsent = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null")?.choice || null; }
  catch { return null; }
};

const OPEN_EVENT = "open-cookie-settings";
// Call from anywhere (e.g. a footer link) to let a visitor change their consent.
export const openCookieSettings = () => window.dispatchEvent(new Event(OPEN_EVENT));

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!getCookieConsent()) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const open = () => setVisible(true);
    window.addEventListener(OPEN_EVENT, open);
    return () => window.removeEventListener(OPEN_EVENT, open);
  }, []);

  const choose = (choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice, at: new Date().toISOString() }));
    } catch { /* storage unavailable */ }
    // Non-essential/analytics loaders should check getCookieConsent() === "all" before running.
    // We currently load no non-essential cookies, so rejecting simply records the choice.
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:max-w-md z-[100]"
          data-testid="cookie-banner"
          role="dialog"
          aria-label="Cookie consent"
        >
          <div className="rounded-2xl border border-neutral-200 bg-white shadow-xl shadow-neutral-900/10 p-5">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-[#EEF2F4] text-[#516670] flex items-center justify-center shrink-0">
                <Cookie className="h-4 w-4" />
              </div>
              <div>
                <p className="font-heading text-sm font-semibold text-neutral-900">We use cookies</p>
                <p className="mt-1 text-xs text-neutral-500 leading-relaxed">
                  We use essential cookies to keep you signed in and the service secure. With your consent we may also use optional analytics to improve Swishr Central. See our{" "}
                  <Link to="/privacy" className="text-[#516670] font-medium underline underline-offset-2 hover:text-[#41545d]" data-testid="cookie-privacy-link">Privacy Policy</Link>.
                </p>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => choose("essential")}
                className="flex-1 rounded-full border-neutral-300 hover:bg-neutral-50"
                data-testid="cookie-reject"
              >
                Essential only
              </Button>
              <Button
                size="sm"
                onClick={() => choose("all")}
                className="flex-1 rounded-full bg-[#516670] hover:bg-[#41545d]"
                data-testid="cookie-accept"
              >
                Accept all
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
